import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  Inject, OnDestroy, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OnDestroy$ } from '@pdtec/ngx-observable-lifecycle';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { filter, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  BrandList,
  ChangedFilterEvent,
  HTTPSearchResultFilter,
  HTTPSearchResultFiltersResponse,
  HTTPSearchResultFilterValue,
  HttpSearchResultFilterPrices,
  HttpSearchResultTag
} from 'src/app/models';
import { ToursStateService } from 'src/app/services/tours-state.service';
import { HotelNameSearchComponent } from '../hotel-name-search/hotel-name-search.component';
import { FilterComponent } from './filter/filter.component';
import {SearchService} from "../../services/search/search.service";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../config/hotel-tours-list-page.config";
import { SmartFiltersComponent } from './smart-filters/smart-filters.component';
import { PriceFilterComponent } from './price-filter/price-filter.component';
import { TagsFilterComponent } from './tags-filter/tags-filter.component';
import { ToursSortComponent } from './sorts/tours-sort.component';
import { HttpSearchService } from '../../services/search/search-requests.service';

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    HotelNameSearchComponent,
    FilterComponent,
    SmartFiltersComponent,
    PriceFilterComponent,
    TagsFilterComponent,
    ToursSortComponent,
  ],

})
export class FiltersComponent extends OnDestroy$ implements OnInit, OnDestroy {
  filters: HTTPSearchResultFilter[] = [];
  filterPrices: HttpSearchResultFilterPrices = null;
  tags: HttpSearchResultTag[] = null;
  onlyRecommendedHotels: HTTPSearchResultFilter;

  private selectedCountSub = new BehaviorSubject<number>(0);
  private destroy$ = new Subject<void>();
  selectedCount$ = this.selectedCountSub.asObservable();

  isShowApplyButton = false;
  isShowHotelSearchInput = true;
  isRenderFilters = true;
  isAllowSmartFilters = false;
  onlySmartFilter = false;
  isTagsLoading = false;

  messages: any = {};

  constructor(
    private searchService: SearchService,
    private state: ToursStateService,
    private cdRef: ChangeDetectorRef,
    private httpSearchService: HttpSearchService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig
  ) {
    super();

    this.isShowHotelSearchInput = this.hotelToursPageConfig.isShowHotelSearchInput;
    this.isRenderFilters = this.hotelToursPageConfig.isRenderFilters;
    this.isAllowSmartFilters = this.hotelToursPageConfig.isAllowSmartFilters;
    this.messages = this.hotelToursPageConfig.translates.filters;
    this.onlyRecommendedHotels = {
      filterKey: 'onlyRecommendedHotels',
      name: 'Рекомендация',
      values: [
        {
          viewValue: 'Ht.kz рекомендует',
          filterValue: true,
          count: 0,
          minPrice: 0,
        } as unknown as HTTPSearchResultFilterValue
      ],
    } as HTTPSearchResultFilter;
  }

  ngOnInit() {
    const prepareFiltersFn = (response: HTTPSearchResultFiltersResponse) => {
      this.searchService.updateToursCount(response?.total || 0);

      if (response?.filters?.length) {
        const order = ['stars', 'ratings', 'nightsList', 'seaLines', 'beachTypes', 'mealIds', 'regionsIds', 'airlines', 'hotelServices'];
        this.filters = [...response.filters].sort((a, b) => {
          const aIndex = order.indexOf(a.filterKey);
          const bIndex = order.indexOf(b.filterKey);
          return (aIndex === -1 ? order.length : aIndex) - (bIndex === -1 ? order.length : bIndex);
        });
      }

      if (response?.prices) {
        this.filterPrices = response.prices;
      }

      this.cdRef.detectChanges();
    };

    this.searchService.filters$.subscribe((response) => {
      prepareFiltersFn(response);

      this.cdRef.detectChanges();
    });

    this.state.clear$.subscribe(() => {
      this.filters = [];
      this.filterPrices = null;
      this.tags = null;
      this.selectedCountSub.next(0);
      this.isShowApplyButton = false;
      this.isTagsLoading = false;

      this.cdRef.detectChanges();
    });

    this.searchService.newSearchStarted$
      .pipe(
        filter(searchId => !!searchId),
        tap(() => {
          this.tags = null;
          this.isTagsLoading = true;
          this.cdRef.detectChanges();
        }),
        mergeMap(searchId =>
          this.httpSearchService.getTags(searchId, {bestHotelOnly: false}).pipe(
            mergeMap(response => {
              if (!response.isDone) {
                return this.searchService.searchIsDone$.pipe(
                  take(1),
                  mergeMap(() => this.httpSearchService.getTags(searchId, {bestHotelOnly: false}))
                );
              }

              return of(response);
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.tags = response.tags;
        this.isTagsLoading = false;
        this.state.setTags(this.tags);
        this.cdRef.detectChanges();
      });

    this.searchService.applyFilters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyFilters(true);
        this.onlySmartFilter = this.state.getState().selectedFilters.onlySmart;
        this.cdRef.detectChanges();
      });
  }

  changeSelectedCount(event: ChangedFilterEvent): void {
    this.onlySmartFilter = event.onlySmartFilter;
    this.state.getState().selectedFilters.onlySmart = event.onlySmartFilter;
    this.state.getState().applyedFilters.onlySmart = event.onlySmartFilter;
    this.selectedCountSub.next(event.applyedCount);

    this.isShowApplyButton = this.hasSelectedFilterChanges();

    this.cdRef.detectChanges();
  }

  onAdditionalFilterChanged(): void {
    this.isShowApplyButton = this.hasSelectedFilterChanges();
    this.cdRef.detectChanges();
  }

  applyFilters(isShort = false): void {
    this.state.applyFilters();
    this.searchService.filtersApplyed();

    const newState = this.state.getState();
    if (newState.applyedFilters.nightsList.length ||
      newState.applyedFilters.regionsIds.length ||
      newState.applyedFilters.stars.length
    ) {
      this.searchService.postfilterSearch();
    }

    this.searchService.setFiltersState(newState.applyedFilters);
    this.selectedCountSub.next(this.state.getState().applyedFiltersCount);

    this.isShowApplyButton = false;
  }

  onResetFilters() {
    this.isShowApplyButton = false;
    this.selectedCountSub.next(0);

    this.state.resetFilters();
    this.searchService.filtersReset();
    this.onlySmartFilter = false;
  }

  trackByFn(_, item: HTTPSearchResultFilter) {
    return item.filterKey;
  }

  get isTicketsBrand(): boolean {
    return this.hotelToursPageConfig.brandId === BrandList.TICKETS_KZ;
  }

  hasSelectedFilters(): boolean {
    return this.state.hasSelectedFilters();
  }

  hasFooterActions(): boolean {
    return this.hasSelectedFilters() || this.isShowApplyButton;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private hasSelectedFilterChanges(): boolean {
    const state = this.state.getState();

    return JSON.stringify(state.selectedFilters) !== JSON.stringify(state.applyedFilters);
  }
}
